// The optional question of the first connection, how the person heard of Sculpt: every signed-in page leads here until
// it is answered or skipped (RequireSession in App.tsx), then on to where the user was going.
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { PublicLayout } from '../components/PublicLayout';
import { Button } from '../components/ui';
import { ApiError, api } from '../services/api';
import { useSession } from '../session';
import { DISCOVERY_SOURCES, type DiscoverySource } from '../types';

export function DiscoveryPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, setUser } = useSession();
  const [source, setSource] = useState<DiscoverySource | null>(null);
  const [busy, setBusy] = useState(false);

  if (!user || user.discovery_asked !== false) return <Navigate to={(location.state as { returnTo?: string } | null)?.returnTo ?? '/'} replace />;

  const answer = async (answered: DiscoverySource | null) => {
    if (busy) return;
    setBusy(true);
    try {
      setUser(await api.answerDiscovery(answered));
    } catch (err) {
      // An expired session goes to the sign-in (session.tsx). Otherwise, an optional question holds nobody back: it
      // comes again the next time the portal opens.
      if (!(err instanceof ApiError && err.status === 401)) setUser({ ...user, discovery_asked: true });
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (source) void answer(source);
  };

  return (
    <PublicLayout title={t('discovery.title')} subtitle={t('discovery.subtitle')}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <fieldset className="flex flex-col gap-2">
          <legend className="sr-only">{t('discovery.title')}</legend>
          {DISCOVERY_SOURCES.map((s) => (
            <label key={s} className={`flex cursor-pointer items-center gap-2.5 rounded-md border bg-white px-3 py-2.5 text-[14px] text-ink hover:bg-hover ${source === s ? 'border-accent' : 'border-line-btn'}`}>
              <input type="radio" name="discovery-source" value={s} checked={source === s} onChange={() => setSource(s)} className="text-accent focus:ring-0" />
              {t(`discovery.sources.${s}`)}
            </label>
          ))}
        </fieldset>
        <Button type="submit" disabled={busy || !source} className="mt-1 w-full">
          {t('discovery.continue')}
        </Button>
        <Button variant="ghost" disabled={busy} className="w-full" onClick={() => void answer(null)}>
          {t('discovery.skip')}
        </Button>
      </form>
    </PublicLayout>
  );
}
